/**
 * SALE-025 — dérogations de remise : demander, suivre, décider.
 *
 * Trois surfaces, un seul contrat : le commercial DEMANDE (et voit ses demandes
 * via `mine`), l'approbateur voit sa FILE et décide, la ligne de devis porte
 * l'état de la demande.
 *
 * ⚠ Les pourcentages et montants arrivent en **chaînes** (décimaux exacts côté
 * serveur). `Math.min("18.00", "25.00")` compare des CHAÎNES en JavaScript et
 * rend « 18.00 » ou « 25.00 » selon l'ordre lexicographique : toute comparaison
 * passe par `coerceToNumber`, jamais par la valeur brute.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { API_BASE, apiFetch } from '@/lib/api'
import { handleApiError } from '@/lib/api-error'

const ENDPOINT = `${API_BASE}/sales`

//: Les SIX états de `quote_discount_approvals.status`, tels que la contrainte
//: `ck_sale025_status` les énumère en base.
//:
//: 🔴 `CONSUMED` manquait ici, et c'est ce qui a rendu le défaut invisible : le
//: type ne le connaissant pas, le `switch` du badge n'avait aucune raison de le
//: traiter, et TypeScript n'avait rien à signaler. Or c'est l'aboutissement
//: NORMAL d'un accord — `consume_grant` y fait passer la demande dès que le
//: commercial repose la remise sur sa ligne. La majorité des dérogations
//: abouties s'affichaient donc « En attente », dans l'onglet « Traitées », sous
//: la réponse du responsable.
export type ApprovalStatus =
    | 'PENDING'
    | 'GRANTED'
    | 'CONSUMED'
    | 'REFUSED'
    | 'CANCELLED'
    | 'EXPIRED'

export interface DiscountApproval {
    id: string
    quote_id: string
    quote_line_id: string | null
    scope: 'LINE' | 'GLOBAL'
    status: ApprovalStatus
    requested_pct: string
    requester_cap_pct: string
    granted_pct: string | null
    justification: string | null
    decision_note: string | null
    /** CAT-014 : la vente passerait sous le prix plancher de la formation. */
    below_floor: boolean
    approver_level: number
    currency: string
    requested_unit_amount: string
    requested_total_amount: string
    floor_unit_amount: string | null
    catalog_base_amount: string | null
    requester_name: string | null
    approver_name: string | null
    /** De quoi la demande parle — la file nomme, elle ne montre pas d'identifiants. */
    quote_reference: string | null
    client_name: string | null
    line_label: string | null
    line_position: number | null
    expires_at: string | null
    created_at: string | null
}

export interface ApproverPreview {
    approver_id: string | null
    approver_name: string | null
    approver_level: number
    requester_cap_pct: string
    /** Message du serveur quand personne n'est éligible — il dit quoi faire. */
    unavailable_reason: string | null
}

export const discountApprovalKeys = {
    all: ['discountApprovals'] as const,
    /**
     * ⚠️ `exclude` fait partie de la CLÉ, jamais fondu dans `status` : « les
     * demandes en attente » et « toutes SAUF celles en attente » sont deux
     * réponses opposées. Les confondre servirait la file traitée depuis le
     * cache de la file à traiter.
     */
    queue: (entityId: string, mine: boolean, status: string | null, exclude: string | null = null) =>
        ['discountApprovals', 'queue', entityId, mine, status, exclude] as const,
    byQuote: (quoteId: string) => ['discountApprovals', 'quote', quoteId] as const,
    approver: (quoteId: string) => ['discountApprovals', 'approver', quoteId] as const,
}

function headers(entityId: string | null | undefined): HeadersInit {
    const h: Record<string, string> = { 'Content-Type': 'application/json' }
    if (entityId) h['X-Entity-Id'] = entityId
    return h
}

/** File de l'approbateur (défaut) ou « mes demandes » côté commercial. */
export function useDiscountApprovals(
    entityId: string | null | undefined,
    params: {
        mine?: boolean
        status?: ApprovalStatus | null
        /**
         * Statut à EXCLURE, appliqué par le SERVEUR.
         *
         * 🔴 L'onglet « Traitées » écartait les `PENDING` dans le navigateur,
         * APRÈS que le serveur eut plafonné à 100 lignes tous statuts
         * confondus : une file portant 40 demandes en attente n'affichait que
         * 60 demandes traitées, sur un historique bien plus long, sans un mot.
         */
        excludeStatus?: ApprovalStatus | null
    } = {},
    options?: { enabled?: boolean },
) {
    const mine = params.mine ?? false
    const status = params.status ?? null
    const excludeStatus = params.excludeStatus ?? null
    return useQuery<DiscountApproval[]>({
        queryKey: discountApprovalKeys.queue(entityId ?? '', mine, status, excludeStatus),
        queryFn: async () => {
            const query = new URLSearchParams()
            if (mine) query.set('mine', 'true')
            if (status) query.set('status', status)
            if (excludeStatus) query.set('exclude_status', excludeStatus)
            const response = await apiFetch(
                `${ENDPOINT}/discount-approvals${query.toString() ? `?${query}` : ''}`,
                { headers: headers(entityId) },
            )
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled: !!entityId && options?.enabled !== false,
        staleTime: 30_000,
    })
}

/** Dérogations d'un devis — la ligne en tire son badge. */
export function useQuoteDiscountApprovals(
    quoteId: string | null,
    entityId: string | null | undefined,
    options?: { enabled?: boolean },
) {
    return useQuery<DiscountApproval[]>({
        queryKey: discountApprovalKeys.byQuote(quoteId ?? ''),
        queryFn: async () => {
            const response = await apiFetch(
                `${ENDPOINT}/quotes/${quoteId}/discount-approvals`,
                { headers: headers(entityId) },
            )
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled: !!quoteId && options?.enabled !== false,
        staleTime: 30_000,
    })
}

/** Qui recevra la demande — résolu AVANT l'envoi, rien n'est créé. */
export function useApproverPreview(
    quoteId: string | null,
    entityId: string | null | undefined,
    options?: { enabled?: boolean },
) {
    return useQuery<ApproverPreview>({
        queryKey: discountApprovalKeys.approver(quoteId ?? ''),
        queryFn: async () => {
            const response = await apiFetch(
                `${ENDPOINT}/quotes/${quoteId}/discount-approvals/approver`,
                { headers: headers(entityId) },
            )
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled: !!quoteId && options?.enabled !== false,
        staleTime: 60_000,
        retry: false,
    })
}

export interface ApprovalRequestPayload {
    quote_line_id: string | null
    requested_pct: string
    requested_unit_amount: string
    requested_total_amount: string
    justification: string
}

export function useRequestDiscountApproval(
    quoteId: string,
    entityId: string | null | undefined,
) {
    const queryClient = useQueryClient()
    return useMutation<DiscountApproval, Error, ApprovalRequestPayload>({
        mutationFn: async body => {
            const response = await apiFetch(`${ENDPOINT}/quotes/${quoteId}/discount-approvals`, {
                method: 'POST',
                headers: headers(entityId),
                body: JSON.stringify(body),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: discountApprovalKeys.all })
        },
    })
}

export interface ApprovalDecisionPayload {
    /** `null` ⇒ REFUS. Accorder MOINS que demandé est le cas nominal. */
    granted_pct: string | null
    decision_note: string | null
}

export function useDecideDiscountApproval(entityId: string | null | undefined) {
    const queryClient = useQueryClient()
    return useMutation<
        DiscountApproval,
        Error,
        { approvalId: string; decision: ApprovalDecisionPayload }
    >({
        mutationFn: async ({ approvalId, decision }) => {
            const response = await apiFetch(
                `${ENDPOINT}/discount-approvals/${approvalId}/decide`,
                {
                    method: 'POST',
                    headers: headers(entityId),
                    body: JSON.stringify(decision),
                },
            )
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: discountApprovalKeys.all })
        },
    })
}

/** Le DEMANDEUR retire sa demande. L'approbateur, lui, refuse — et son refus
 *  est une décision tracée, pas un retrait. */
export function useCancelDiscountApproval(entityId: string | null | undefined) {
    const queryClient = useQueryClient()
    return useMutation<void, Error, string>({
        mutationFn: async approvalId => {
            const response = await apiFetch(
                `${ENDPOINT}/discount-approvals/${approvalId}/cancel`,
                { method: 'POST', headers: headers(entityId) },
            )
            if (!response.ok) await handleApiError(response)
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: discountApprovalKeys.all })
        },
    })
}
